// Top-level routing + the three app states:
//   1. No backend configured  -> SetupNotice
//   2. Not signed in           -> Login
//   3. Signed in               -> Layout shell with the page routes inside
//
// To add a page: import it, add a <Route> below, and add a NAV entry in Layout.

import { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { isSupabaseConfigured } from "./lib/supabase";
import { Spinner } from "./components/ui";
import { useAuth } from "./auth/AuthProvider";
import { setSeasonalFactors } from "./lib/forecast/engine";
import { getSeasonal } from "./features/seasonal/seasonalApi";
import Login from "./auth/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import SetupNotice from "./pages/SetupNotice";
import HubList from "./features/hubs/HubList";
import BranchList from "./features/branches/BranchList";
import HubEventList from "./features/hubevents/HubEventList";
import EventImpactPage from "./features/hubevents/EventImpactPage";
import CustomerList from "./features/customers/CustomerList";
import ForecastPage from "./features/forecast/ForecastPage";
import ForecastWorkbench from "./features/forecast/ForecastWorkbench";
import YearlyPage from "./features/forecast/YearlyPage";
import YearOverviewPage from "./features/forecast/YearOverviewPage";
import VolumeAvailabilityPage from "./features/forecast/VolumeAvailabilityPage";
import HubDashboardPage from "./features/forecast/HubDashboardPage";
import BranchForecastPage from "./features/forecast/BranchForecastPage";
import ForecastLockPage from "./features/forecast/ForecastLockPage";
import ForecastPlannerPage from "./features/forecast/ForecastPlannerPage";
import AnalyticsPage from "./features/analytics/AnalyticsPage";
import CommandCenterPage from "./features/analytics/CommandCenterPage";
import RoutingMapPage from "./features/network/RoutingMapPage";
import GuidelinesPage from "./features/docs/GuidelinesPage";
import CalibrationOverview from "./features/calibration/CalibrationOverview";
import SeasonalPage from "./features/seasonal/SeasonalPage";
import ImportActualsPage from "./features/actuals/ImportActualsPage";
import BranchActualsImportPage from "./features/actuals/BranchActualsImportPage";

export default function App() {
  const { session, loading, accountsLoading, activeAccount } = useAuth();
  const acctId = activeAccount?.accountId ?? null;

  // Load the account's calibrated seasonal curve once so every forecast uses it.
  useEffect(() => {
    if (!acctId) {
      setSeasonalFactors(null);
      return;
    }
    let alive = true;
    getSeasonal(acctId)
      .then((f) => {
        if (alive) setSeasonalFactors(f);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [acctId]);

  if (!isSupabaseConfigured) return <SetupNotice />;

  if (loading || (session && accountsLoading)) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!session) return <Login />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="hubs" element={<HubList />} />
        <Route path="branches" element={<BranchList />} />
        <Route path="events" element={<HubEventList />} />
        <Route path="event-impact" element={<EventImpactPage />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="forecast" element={<ForecastPage />} />
        <Route path="workbench" element={<ForecastWorkbench />} />
        <Route path="volume-availability" element={<VolumeAvailabilityPage />} />
        <Route path="hub-dashboard" element={<HubDashboardPage />} />
        <Route path="branch-forecast" element={<BranchForecastPage />} />
        <Route path="forecast-lock" element={<ForecastLockPage />} />
        <Route path="forecast-planner" element={<ForecastPlannerPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="command-center" element={<CommandCenterPage />} />
        <Route path="routing-map" element={<RoutingMapPage />} />
        <Route path="year-overview" element={<YearOverviewPage />} />
        <Route path="yearly" element={<YearlyPage />} />
        <Route path="calibration" element={<CalibrationOverview />} />
        <Route path="seasonal" element={<SeasonalPage />} />
        <Route path="import-actuals" element={<ImportActualsPage />} />
        <Route path="import-branch-actuals" element={<BranchActualsImportPage />} />
        <Route path="guidelines" element={<GuidelinesPage />} />
      </Route>
    </Routes>
  );
}
